import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, X, ArrowRight, Calendar, Mail, Users, MapPin, SlidersHorizontal,
  FileText, CheckCircle, XCircle, LayoutGrid, List as ListIcon,
  MessageCircle, ChevronLeft, ChevronRight, MoreVertical, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ClientsStatusBar from "@/components/clients/ClientsStatusBar";
import ClientsFiltersSheet, { DEFAULT_FILTERS, countActiveFilters } from "@/components/clients/ClientsFiltersSheet";
import SortMenu from "@/components/clients/SortMenu";
import ClientQuickPanel from "@/components/clients/ClientQuickPanel";
import { ClientsTable, ClientRowMobile } from "@/components/clients/ClientsList";
import { fmtDateIT, relTime, useMediaQuery } from "@/components/clients/clientsUi";

// ═══════════════════════════════════════════════════════════════════════════
// CRM v2.1 — Gestione Clienti (responsive)
// Hierarchy: title → search → [Filtri] [Ordina] → compact status bar → list.
// Desktop: operative table + quick panel on the right (no page change).
// Mobile: compact rows, full-screen quick detail sheet, filters in a sheet.
// Server-side pagination/filter/sort/search via GET /clients/list.
// ═══════════════════════════════════════════════════════════════════════════

const EMPTY = {
  name: "", partner_name: "", email: "", phone: "", wedding_date: "",
  venue: "", status: "lead", budget: 0, notes: "",
};

const PAGE_SIZE = 6;
const WORKFLOW_KEYS = ["draft", "quote_sent", "contract_signed", "completed", "rejected"];

// Desktop-only Card/List preference (mobile ALWAYS uses the compact layout).
function useViewMode() {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("clients_view_mode") || "list"; } catch { return "list"; }
  });
  useEffect(() => {
    try { localStorage.setItem("clients_view_mode", mode); } catch { /* noop */ }
  }, [mode]);
  return [mode, setMode];
}

function useDebounced(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function initialStatus(sp) {
  // Legacy deep-link `?filter=leads` → the real "Nuove" view (not a dead param).
  if (sp.get("filter") === "leads") return "new";
  const s = sp.get("status");
  return s && ["all", "new", "attention", ...WORKFLOW_KEYS].includes(s) ? s : "all";
}

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 767px)");

  // ── Filter state (URL-synced) ────────────────────────────────────────────
  const [status, setStatus] = useState(() => initialStatus(searchParams));
  const [filters, setFilters] = useState(() => ({
    year: searchParams.get("year") || "all",
    operator: searchParams.get("operator") || "all",
    dateRange: searchParams.get("date_range") || "all",
    paymentStatus: searchParams.get("payment") || "all",
  }));
  const [sort, setSort] = useState(searchParams.get("sort") || "wedding_date_asc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search, 350);
  const [viewMode, setViewMode] = useViewMode();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // ── Server data ──────────────────────────────────────────────────────────
  const [data, setData] = useState({ items: [], total: 0, counts_by_status: {}, years: [] });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  // LeadsContext stays the GLOBAL badge (Dashboard/bell); the Clients page
  // shows what the backend declares for the current filters (data.new_count).
  const attention = data.attention || null;

  useEffect(() => { setPage(1); }, [status, filters, debouncedSearch, sort]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (filters.year !== "all") p.set("year", filters.year);
    if (filters.operator !== "all") p.set("operator", filters.operator);
    if (filters.dateRange !== "all") p.set("date_range", filters.dateRange);
    if (filters.paymentStatus !== "all") p.set("payment", filters.paymentStatus);
    if (sort !== "wedding_date_asc") p.set("sort", sort);
    setSearchParams(p, { replace: true });
  }, [status, filters, sort, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort };
      if (WORKFLOW_KEYS.includes(status)) params.status = status;
      if (status === "new") params.unread_leads = true;
      if (filters.year !== "all") params.year = filters.year;
      if (filters.operator !== "all") params.operator_id = filters.operator;
      if (filters.dateRange !== "all") params.date_range = filters.dateRange;
      if (filters.paymentStatus !== "all") params.payment_status = filters.paymentStatus;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      const { data: res } = await api.get("/clients/list", { params });
      setData(res);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[clients] /clients/list failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, status, filters, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/team").then((t) => setTeam(t.data || [])).catch(() => setTeam([]));
  }, []);

  // Quick panel marked an unread lead as read → reflect it in the list at once.
  const onMarkedRead = useCallback((id) => {
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === id ? { ...it, is_unread_lead: false } : it)) }));
    setSelected((s) => (s && s.id === id ? { ...s, is_unread_lead: false } : s));
  }, []);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const rangeStart = data.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(page * PAGE_SIZE, data.total);
  const activeFilterCount = countActiveFilters(filters);
  const hasAnyFilter = status !== "all" || activeFilterCount > 0 || !!search;
  const resetAll = () => { setStatus("all"); setFilters(DEFAULT_FILTERS); setSearch(""); };

  const saveNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/clients", { ...form, budget: parseFloat(form.budget) || 0 });
      toast.success("Cliente creata");
      setOpenNew(false); setForm(EMPTY); load();
    } catch (err) {
      if (err?.response?.status === 402) return;
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const openClient = (c) => setSelected(c);
  const showDesktopPanel = !isMobile;

  return (
    <div className="space-y-4" data-testid="clients-page">
      {/* ═══ Header ═══════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="label-uppercase hidden md:inline">Gestione</span>
          <h1 className="font-serif-heading text-3xl sm:text-4xl tracking-tight md:mt-1">
            <span className="md:hidden">Clienti</span><span className="hidden md:inline">Gestione Clienti</span>
          </h1>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="rounded-md h-10" data-testid="clients-new-button">
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.7} />Nuovo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif-heading text-2xl">Nuovo cliente</DialogTitle>
              <DialogDescription>Compila i dati principali. Potrai completare la scheda in seguito.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveNew} className="space-y-4" data-testid="clients-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-uppercase">Nome sposa *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="client-name-input" />
                </div>
                <div>
                  <Label className="label-uppercase">Partner</Label>
                  <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="label-uppercase">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="label-uppercase">Telefono</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="label-uppercase">Data matrimonio</Label>
                  <Input type="date" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="label-uppercase">Location</Label>
                  <Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>
              <div>
                <Label className="label-uppercase">Note</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={3} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpenNew(false)}>Annulla</Button>
                <Button type="submit" disabled={busy} className="rounded-sm">{busy ? "Salvataggio…" : "Crea cliente"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ═══ Search ═══════════════════════════════════════════════════════ */}
      <div className="relative" data-testid="clients-search-wrapper">
        <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
        <Input
          type="text" value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca nome, telefono, email…"
          aria-label="Cerca cliente per nome, partner, email, telefono o location"
          className="pl-9 pr-9 rounded-md h-11 bg-white border-border focus-visible:border-primary focus-visible:ring-0"
          data-testid="clients-search-input"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Cancella ricerca"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-400 hover:text-neutral-700"
            data-testid="clients-search-clear">
            <X className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* ═══ Filtri | Ordina (+ Card/Lista on desktop) ════════════════════ */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          className={`flex-1 md:flex-none h-11 px-4 rounded-md border bg-white text-sm inline-flex items-center justify-center gap-2 hover:border-neutral-300 ${activeFilterCount ? "border-primary text-primary" : "border-border"}`}
          data-testid="clients-filters-button"
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.6} /> Filtri
          {activeFilterCount > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-semibold inline-flex items-center justify-center" data-testid="clients-filters-count">{activeFilterCount}</span>}
        </button>
        <SortMenu value={sort} onChange={setSort} className="flex-1 md:flex-none" />
        {hasAnyFilter && (
          <button type="button" onClick={resetAll} className="hidden md:inline text-[12px] text-neutral-500 underline hover:text-neutral-800 ml-1" data-testid="clear-filters">
            Azzera
          </button>
        )}
        {!isMobile && (
          <div className="ml-auto inline-flex rounded-md border border-border bg-white p-0.5" data-testid="view-toggle">
            <button type="button" onClick={() => setViewMode("list")} className={`inline-flex items-center gap-1.5 px-3 h-9 rounded text-[13px] ${viewMode === "list" ? "bg-primary/10 text-primary font-medium" : "text-neutral-600"}`} data-testid="view-list">
              <ListIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Lista
            </button>
            <button type="button" onClick={() => setViewMode("card")} className={`inline-flex items-center gap-1.5 px-3 h-9 rounded text-[13px] ${viewMode === "card" ? "bg-primary/10 text-primary font-medium" : "text-neutral-600"}`} data-testid="view-card">
              <LayoutGrid className="w-3.5 h-3.5" strokeWidth={1.5} /> Card
            </button>
          </div>
        )}
      </div>

      <ClientsFiltersSheet
        open={filtersOpen} onOpenChange={setFiltersOpen} filters={filters} onApply={setFilters}
        years={data.years} team={team} mobile={isMobile}
      />

      {/* ═══ Status bar ═══════════════════════════════════════════════════ */}
      <ClientsStatusBar
        value={status} onChange={setStatus}
        counts={data.counts_by_status} unreadCount={data.new_count ?? 0} attentionTotal={data.attention_count ?? 0}
      />

      {status === "attention" && (
        <div className="border border-amber-200 bg-amber-50/60 rounded-md p-4" data-testid="attention-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-amber-900 font-medium">Richiede attenzione</span>
            <span className="text-[11px] rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 tabular-nums">{attention?.total || 0}</span>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-[13px]">
            <AttentionRow label="Preventivi da ricontattare" n={attention?.quotes_to_recontact} onClick={() => setStatus("quote_sent")} />
            <AttentionRow label="Contratti da firmare" n={attention?.contracts_to_sign} onClick={() => setStatus("quote_sent")} />
            <AttentionRow label="Prove da fissare" n={attention?.trials_to_schedule} onClick={() => setStatus("contract_signed")} />
            <AttentionRow label="Saldi mancanti" n={attention?.balance_missing} onClick={() => { setStatus("all"); setFilters({ ...filters, paymentStatus: "balance_due" }); }} />
          </ul>
        </div>
      )}

      {/* ═══ List + quick panel ═══════════════════════════════════════════ */}
      <div className={`grid grid-cols-1 gap-4 ${showDesktopPanel && viewMode === "list" ? "lg:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
        <div className="min-w-0">
          {loading && data.items.length === 0 && <div className="text-sm text-neutral-500 py-12 text-center" data-testid="clients-loading">Caricamento…</div>}
          {!loading && data.items.length === 0 && (
            <div className="border border-dashed border-border rounded-md py-14 text-center text-sm text-neutral-500 bg-white" data-testid="clients-empty">
              {search ? "Nessun cliente corrisponde alla ricerca." : status === "new" ? "Nessuna nuova richiesta da leggere." : status === "attention" ? "Nessun cliente da gestire." : "Nessun cliente in questo filtro."}
            </div>
          )}
          {data.items.length > 0 && isMobile && (
            <div className="space-y-2" data-testid="clients-mobile-list">
              {data.items.map((c) => <ClientRowMobile key={c.id} c={c} onSelect={openClient} />)}
            </div>
          )}
          {data.items.length > 0 && !isMobile && viewMode === "list" && (
            <ClientsTable items={data.items} selectedId={selected?.id} onSelect={openClient} />
          )}
          {data.items.length > 0 && !isMobile && viewMode === "card" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-grid">
              {data.items.map((c) => <ClientCard key={c.id} c={c} onOpen={() => navigate(`/app/clients/${c.id}`)} onStatusChanged={load} />)}
            </div>
          )}

          {data.total > PAGE_SIZE && (
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3" data-testid="pagination">
              <span className="text-[12px] text-neutral-500">Mostra {rangeStart}–{rangeEnd} di {data.total} clienti</span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-8 h-8 rounded-md border border-border bg-white text-neutral-700 disabled:opacity-40 hover:border-primary hover:text-primary"
                  data-testid="page-prev" aria-label="Pagina precedente">
                  <ChevronLeft className="w-4 h-4 mx-auto" strokeWidth={1.5} />
                </button>
                {pageWindow(page, totalPages).map((n, i) => (
                  n === "…" ? (
                    <span key={`gap-${i}`} className="w-8 h-8 grid place-items-center text-neutral-400">…</span>
                  ) : (
                    <button key={n} onClick={() => setPage(n)}
                      className={`w-8 h-8 rounded-md border text-[13px] ${n === page ? "bg-primary/10 border-primary text-primary font-medium" : "bg-white border-border text-neutral-700 hover:border-primary"}`}
                      data-testid={`page-${n}`}>{n}</button>
                  )
                ))}
                <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-8 h-8 rounded-md border border-border bg-white text-neutral-700 disabled:opacity-40 hover:border-primary hover:text-primary"
                  data-testid="page-next" aria-label="Pagina successiva">
                  <ChevronRight className="w-4 h-4 mx-auto" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </div>

        {showDesktopPanel && viewMode === "list" && (
          <aside className="hidden lg:block lg:sticky lg:top-4 self-start" data-testid="clients-quick-aside">
            <div className="border border-border rounded-md bg-neutral-50/40 min-h-[420px]">
              {selected ? (
                <ClientQuickPanel key={selected.id} item={selected} onClose={() => setSelected(null)} onMarkedRead={onMarkedRead} />
              ) : (
                <div className="h-[420px] flex flex-col items-center justify-center text-center px-6 text-sm text-neutral-500" data-testid="quick-panel-placeholder">
                  <Users className="w-6 h-6 text-neutral-300 mb-3" strokeWidth={1.4} />
                  Seleziona una cliente per vedere il dettaglio rapido.
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* Mobile full-screen quick detail */}
      {isMobile && selected && (
        <div className="fixed inset-0 z-50 bg-background overflow-y-auto" data-testid="clients-mobile-sheet">
          <ClientQuickPanel key={selected.id} item={selected} mobile onClose={() => setSelected(null)} onMarkedRead={onMarkedRead} />
        </div>
      )}
    </div>
  );
}

function pageWindow(current, total) {
  const w = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) w.push(i);
    return w;
  }
  w.push(1);
  if (current > 3) w.push("…");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) w.push(i);
  if (current < total - 2) w.push("…");
  w.push(total);
  return w;
}

function AttentionRow({ label, n, onClick }) {
  return (
    <li>
      <button type="button" onClick={onClick}
        className="w-full flex items-center justify-between hover:bg-white/70 rounded px-2 py-1.5 text-left"
        data-testid={`attention-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        <span className="text-neutral-700">{label}</span>
        <span className={`tabular-nums ${n ? "text-primary font-semibold" : "text-neutral-400"}`}>{n ?? 0}</span>
      </button>
    </li>
  );
}

const REJECTION_REASONS_UI = [
  { key: "price", label: "Prezzo" },
  { key: "date_unavailable", label: "Data non disponibile" },
  { key: "chose_another", label: "Ha scelto un altro professionista" },
  { key: "no_response", label: "Mancata risposta" },
  { key: "schedule_change", label: "Cambio programma" },
  { key: "other", label: "Altro" },
];

// ─── Card ─────────────────────────────────────────────────────────────────
function ClientCard({ c, onOpen, onStatusChanged }) {
  const phoneDigits = String(c.phone || "").replace(/\D/g, "");
  const canWhatsApp = phoneDigits.length >= 8;
  const canEmail = Boolean(c.email);
  const waHref = canWhatsApp ? `https://wa.me/${phoneDigits}` : "#";
  const mailHref = canEmail ? `mailto:${c.email}` : "#";
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doChangeStatus = async (target, reason, freeText) => {
    setBusy(true);
    try {
      await api.post(`/clients/${c.id}/status`, {
        target_status: target,
        reason: reason || null,
        reason_free_text: freeText || null,
      });
      toast.success(`Stato aggiornato a ${target}`);
      onStatusChanged && onStatusChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const canReject = ["draft", "quote_sent", "contract_signed"].includes(c.workflow_status);
  const canComplete = c.workflow_status === "contract_signed";
  const canReopen = c.workflow_status === "rejected";
  const canUncomplete = c.workflow_status === "completed";

  return (
    <div className="border border-border rounded-md bg-white overflow-hidden flex flex-col" data-testid={`client-card-${c.id}`}>
      <div className="p-4 flex-1">
        <div className="flex items-center justify-between mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded text-[10px] font-semibold uppercase tracking-wider border ${c.workflow_status_class}`}
            data-testid={`badge-${c.id}`}>
            {c.workflow_status_label}
          </span>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="text-neutral-400 hover:text-neutral-700 p-1" aria-label="Altre azioni" data-testid={`card-menu-${c.id}`}>
                  <MoreVertical className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md">
                {canReject && (
                  <DropdownMenuItem onClick={() => setRejectOpen(true)} className="text-violet-700" data-testid={`menu-reject-${c.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Rifiuta pratica
                  </DropdownMenuItem>
                )}
                {canComplete && (
                  <DropdownMenuItem onClick={() => { if (window.confirm("Segnare la pratica come TERMINATA?")) doChangeStatus("completed"); }} className="text-rose-700" data-testid={`menu-complete-${c.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Segna terminato
                  </DropdownMenuItem>
                )}
                {canReopen && (
                  <DropdownMenuItem onClick={() => doChangeStatus("draft")} data-testid={`menu-reopen-${c.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Riapri (→ Bozza)
                  </DropdownMenuItem>
                )}
                {canUncomplete && (
                  <DropdownMenuItem onClick={() => { if (window.confirm("Annullare lo stato TERMINATO?")) doChangeStatus("contract_signed"); }} data-testid={`menu-uncomplete-${c.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Annulla Terminato
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onOpen}>Apri scheda</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button type="button" onClick={onOpen} className="text-neutral-400 hover:text-primary" aria-label="Apri scheda cliente" data-testid={`card-arrow-${c.id}`}>
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <h3 className="font-serif-heading text-xl leading-tight truncate">{c.name || "—"}</h3>
        {c.partner_name && <div className="text-sm text-neutral-600 mt-0.5 truncate">& {c.partner_name}</div>}

        <div className="mt-3 space-y-1.5 text-[13px] text-neutral-700">
          <Row icon={Calendar} data-testid={`row-date-${c.id}`}>
            {fmtDateIT(c.wedding_date)}
            <span className="text-neutral-400 ml-2">Matrimonio</span>
          </Row>
          {c.email && <Row icon={Mail}><span className="truncate">{c.email}</span></Row>}
          {c.operators?.length > 0 && (
            <Row icon={Users}>
              <span className="truncate">Operatori: {c.operators.map((o) => o.name).join(", ")}</span>
            </Row>
          )}
          {c.venue && <Row icon={MapPin}><span className="truncate">{c.venue}</span></Row>}
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px]">
          <span className="text-neutral-500">Ultima attività: <span className="text-neutral-700">{relTime(c.last_activity_at) || "—"}</span></span>
          {c.payment_status_label && (
            <span className="text-neutral-500">
              Pagamento: <span className={`${c.payment_status === "balance_due" ? "text-rose-700 font-medium" : c.payment_status === "paid" ? "text-emerald-700 font-medium" : "text-neutral-700"}`}>{c.payment_status_label}</span>
            </span>
          )}
        </div>
      </div>
      {/* Footer actions */}
      <div className="border-t border-border grid grid-cols-4 divide-x divide-border text-[12px]">
        <button type="button" onClick={onOpen} className="py-2.5 hover:bg-neutral-50 inline-flex items-center justify-center gap-1.5 text-neutral-700" data-testid={`action-open-${c.id}`}>
          <span className="text-neutral-500">▹</span> Apri
        </button>
        <a
          href={waHref} target="_blank" rel="noopener noreferrer"
          className={`py-2.5 inline-flex items-center justify-center gap-1.5 ${canWhatsApp ? "text-neutral-700 hover:bg-neutral-50" : "text-neutral-300 cursor-not-allowed"}`}
          onClick={(e) => { if (!canWhatsApp) e.preventDefault(); }}
          aria-disabled={!canWhatsApp} data-testid={`action-whatsapp-${c.id}`}
        >
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
        </a>
        <a
          href={mailHref}
          className={`py-2.5 inline-flex items-center justify-center gap-1.5 ${canEmail ? "text-neutral-700 hover:bg-neutral-50" : "text-neutral-300 cursor-not-allowed"}`}
          onClick={(e) => { if (!canEmail) e.preventDefault(); }}
          aria-disabled={!canEmail} data-testid={`action-email-${c.id}`}
        >
          <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Email
        </a>
        <Link to={`/app/clients/${c.id}?tab=preventivo`} className="py-2.5 hover:bg-neutral-50 inline-flex items-center justify-center gap-1.5 text-neutral-700" data-testid={`action-quote-${c.id}`}>
          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> {c.has_quote ? "Preventivo" : "Crea preventivo"}
        </Link>
      </div>
      {/* Reject dialog */}
      <RejectDialog open={rejectOpen} onClose={() => setRejectOpen(false)} onConfirm={async (reason, freeText) => {
        await doChangeStatus("rejected", reason, freeText);
        setRejectOpen(false);
      }} busy={busy} clientName={c.name} />
    </div>
  );
}

function RejectDialog({ open, onClose, onConfirm, busy, clientName }) {
  const [reason, setReason] = useState("price");
  const [freeText, setFreeText] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-heading text-xl">Rifiuta pratica</DialogTitle>
          <DialogDescription>
            Stai per marcare la pratica di <strong>{clientName || "questa cliente"}</strong> come RIFIUTATA.
            La cliente resterà nello storico CRM.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="label-uppercase">Motivo del rifiuto</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="h-9 rounded-md bg-white" data-testid="reject-reason-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REJECTION_REASONS_UI.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {reason === "other" && (
            <div>
              <Label className="label-uppercase">Nota (facoltativa)</Label>
              <Textarea value={freeText} onChange={(e) => setFreeText(e.target.value)} className="mt-1 rounded-sm" rows={3} data-testid="reject-freetext" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Annulla</Button>
          <Button disabled={busy} onClick={() => onConfirm(reason, freeText)} className="rounded-sm bg-violet-700 hover:bg-violet-800" data-testid="reject-confirm">
            {busy ? "…" : "Rifiuta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-neutral-400 mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
